<template>
  <div class="pt-4 px-4">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>

    <div v-if="!loading">
      <v-layout row align-center class="mb-4" wrap>
        <v-flex xs12 sm6>
          <div class="display-1 font-weight-bold">Student Flags</div>
          <div class="grey--text mt-1">
            Import a student set, then filter route designs by those flags
          </div>
        </v-flex>
        <v-flex
          xs12
          sm6
          style="display:flex;align-items:center;justify-content:flex-end;gap:12px"
        >
          <v-autocomplete
            v-model="selectedTerm"
            :items="terms"
            label="Term"
            dense
            outlined
            hide-details
            clearable
            style="max-width:180px"
            @change="onTermChange"
          ></v-autocomplete>
          <v-autocomplete
            v-model="selectedFlags"
            :items="flagNames"
            label="Flags"
            dense
            outlined
            hide-details
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            style="max-width:280px"
            @change="loadFlags"
          ></v-autocomplete>
          <v-btn
            color="success"
            outline
            @click="$router.push({ name: 'RouteDesigns' })"
          >
            <v-icon left>mdi-map-marker-distance</v-icon>
            Route Designs
          </v-btn>
          <v-btn color="success" @click="openImportDialog">
            <v-icon left>mdi-upload</v-icon>
            Import CSV
          </v-btn>
        </v-flex>
      </v-layout>

      <v-card elevation="2">
        <v-card-title class="pt-2">
          <v-btn
            color="error"
            outline
            :disabled="selected.length === 0"
            @click="confirmDelete"
          >
            <v-icon left small>mdi-delete</v-icon>
            Delete ({{ selected.length }})
          </v-btn>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            clearable
            style="max-width:300px"
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="rows"
          :search="search"
          :pagination.sync="pagination"
          :rows-per-page-items="[25, 50, 100]"
          item-key="id"
          select-all
          v-model="selected"
          class="pl-2"
        >
          <template v-slot:items="props">
            <tr>
              <td>
                <v-checkbox
                  v-model="props.selected"
                  color="success"
                  hide-details
                ></v-checkbox>
              </td>
              <td>{{ props.item.studentID }}</td>
              <td>{{ displayName(props.item) }}</td>
              <td>{{ props.item.className }}</td>
              <td>{{ props.item.building }}</td>
              <td>{{ props.item.branch }}</td>
              <td>{{ props.item.address }}</td>
              <td>{{ props.item.term }}</td>
              <td>
                <v-chip small class="ma-0" color="amber lighten-4">{{
                  props.item.flag
                }}</v-chip>
              </td>
              <td>{{ props.item.createdBy }}</td>
              <td>{{ formatDate(props.item.createdOn) }}</td>
            </tr>
          </template>
          <template v-slot:no-data>
            <div class="text-xs-center pa-6 grey--text">
              <v-icon large color="grey lighten-1">mdi-flag-outline</v-icon>
              <div class="mt-2">No student flags for this filter</div>
              <v-btn color="success" class="mt-3" @click="openImportDialog">
                Import CSV
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="importDialog" max-width="1100" persistent>
      <v-card>
        <v-card-title class="headline success white--text pa-3">
          Import Student Flags
        </v-card-title>
        <v-card-text class="pt-3">
          <div class="body-1 mb-2">
            File must have exactly three columns:
            <strong>StudentID</strong>, <strong>Term</strong> (e.g. RG2027),
            and <strong>Flag</strong>. Names and addresses are matched from the
            student table after import. One flag per student per term; a
            re-import updates the existing flag. Leading zeros on StudentID
            (0035126) are restored if Excel dropped them.
          </div>

          <input
            ref="flagFile"
            type="file"
            accept=".csv,.xlsx,.xls"
            style="display:none"
            @change="onFilePicked"
          />
          <v-btn color="primary" outline @click="$refs.flagFile.click()">
            <v-icon left>mdi-file-upload</v-icon>
            Choose file
          </v-btn>
          <v-btn
            color="success"
            outline
            class="ml-2"
            @click="downloadSample"
          >
            <v-icon left>mdi-file-download</v-icon>
            Download sample
          </v-btn>
          <span v-if="fileName" class="ml-2 body-1">{{ fileName }}</span>

          <v-progress-linear
            v-if="previewing || savingImport"
            indeterminate
            color="primary"
            class="mt-3"
          ></v-progress-linear>

          <v-alert
            :value="!!previewError"
            type="error"
            class="mt-3"
            @input="previewError = ''"
          >
            {{ previewError }}
          </v-alert>

          <div v-if="preview && preview.rows && preview.rows.length" class="mt-3">
            <v-layout row wrap class="mb-2">
              <v-flex class="pr-2 mb-1">
                <v-chip small color="green lighten-4"
                  >New {{ preview.okCount }}</v-chip
                >
              </v-flex>
              <v-flex class="pr-2 mb-1">
                <v-chip small color="orange lighten-4"
                  >Update {{ preview.updateCount }}</v-chip
                >
              </v-flex>
              <v-flex class="pr-2 mb-1">
                <v-chip small color="blue-grey lighten-4"
                  >Unchanged {{ preview.unchangedCount }}</v-chip
                >
              </v-flex>
              <v-flex class="pr-2 mb-1">
                <v-chip small color="red lighten-4"
                  >Not found {{ preview.notFoundCount }}</v-chip
                >
              </v-flex>
              <v-flex class="pr-2 mb-1">
                <v-chip small color="red lighten-4"
                  >Invalid {{ preview.invalidCount }}</v-chip
                >
              </v-flex>
              <v-flex class="mb-1">
                <v-chip small color="purple lighten-4"
                  >Duplicate {{ preview.duplicateCount }}</v-chip
                >
              </v-flex>
            </v-layout>

            <v-data-table
              :headers="previewHeaders"
              :items="preview.rows"
              :rows-per-page-items="[15, 25, 50]"
              class="elevation-0"
            >
              <template v-slot:items="props">
                <tr>
                  <td>{{ props.item.rowNumber }}</td>
                  <td>{{ props.item.studentID }}</td>
                  <td>{{ props.item.term }}</td>
                  <td>{{ props.item.flag }}</td>
                  <td>{{ displayName(props.item) }}</td>
                  <td>
                    <v-chip
                      small
                      :color="statusColor(props.item.status)"
                      text-color="white"
                    >
                      {{ props.item.status }}
                    </v-chip>
                  </td>
                  <td>
                    <span v-if="props.item.existingFlag" class="caption">
                      was {{ props.item.existingFlag }}
                    </span>
                    <span v-if="props.item.error" class="error--text caption">
                      {{ props.item.error }}
                    </span>
                    <span
                      v-if="props.item.warning"
                      class="orange--text caption"
                    >
                      {{ props.item.warning }}
                    </span>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeImport">Cancel</v-btn>
          <v-btn
            color="success"
            :loading="savingImport"
            :disabled="!canSaveImport"
            @click="saveImport"
          >
            Import {{ importableCount }} row(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Delete flags?</v-card-title>
        <v-card-text>
          Delete {{ selected.length }} selected flag
          {{ selected.length === 1 ? "row" : "rows" }}?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deleteDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="error" :loading="deleting" @click="doDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-alert
      :value="!!errorMessage"
      type="error"
      @input="errorMessage = ''"
      class="mt-4"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "StudentFlags",
  data() {
    return {
      loading: true,
      terms: [],
      selectedTerm: "",
      selectedFlags: [],
      flagNames: [],
      rows: [],
      selected: [],
      search: "",
      pagination: { rowsPerPage: 25, sortBy: "flag", descending: false },
      headers: [
        { text: "Student ID", value: "studentID" },
        { text: "Name", value: "engName" },
        { text: "Class", value: "className" },
        { text: "Building", value: "building" },
        { text: "Branch", value: "branch" },
        { text: "Address", value: "address" },
        { text: "Term", value: "term" },
        { text: "Flag", value: "flag" },
        { text: "Created By", value: "createdBy" },
        { text: "Created", value: "createdOn" },
      ],
      importDialog: false,
      fileName: "",
      previewing: false,
      savingImport: false,
      preview: null,
      previewError: "",
      deleteDialog: false,
      deleting: false,
      errorMessage: "",
    };
  },
  computed: {
    previewHeaders() {
      return [
        { text: "Row", value: "rowNumber" },
        { text: "Student ID", value: "studentID" },
        { text: "Term", value: "term" },
        { text: "Flag", value: "flag" },
        { text: "Matched Name", value: "engName" },
        { text: "Status", value: "status" },
        { text: "Notes", value: "error", sortable: false },
      ];
    },
    importableCount() {
      return this.importableRows().length;
    },
    canSaveImport() {
      return this.importableCount > 0 && !this.previewing;
    },
  },
  async created() {
    try {
      this.loading = true;
      const terms = await this.$http.get("api/allStudents/getRegTerms");
      this.terms = terms || [];
      const stored = localStorage.getItem("studentFlags.selectedTerm");
      this.selectedTerm =
        stored && this.terms.indexOf(stored) !== -1
          ? stored
          : this.terms.length
          ? this.terms[this.terms.length - 1]
          : "";
      await this.loadFlagNames();
      await this.loadFlags();
    } catch (e) {
      console.error(e);
      this.errorMessage = "Failed to load student flags";
    } finally {
      this.loading = false;
    }
  },
  methods: {
    displayName(item) {
      const eng = [item.engName, item.lastName].filter(Boolean).join(" ");
      return eng || item.hebName || "";
    },
    formatDate(val) {
      if (!val) return "";
      return new Date(val).toLocaleString();
    },
    statusColor(status) {
      if (status === "ok") return "green darken-1";
      if (status === "update") return "orange darken-2";
      if (status === "unchanged") return "blue-grey";
      if (status === "duplicate") return "purple";
      return "red darken-1";
    },
    async onTermChange() {
      if (this.selectedTerm) {
        localStorage.setItem("studentFlags.selectedTerm", this.selectedTerm);
      } else {
        localStorage.removeItem("studentFlags.selectedTerm");
      }
      this.selectedFlags = [];
      await this.loadFlagNames();
      await this.loadFlags();
    },
    async loadFlagNames() {
      const term = this.selectedTerm
        ? "?term=" + encodeURIComponent(this.selectedTerm)
        : "";
      const names = await this.$http.get(
        "api/studentFlags/getFlagNames" + term
      );
      this.flagNames = names || [];
    },
    async loadFlags() {
      const params = [];
      if (this.selectedTerm) {
        params.push("term=" + encodeURIComponent(this.selectedTerm));
      }
      if (this.selectedFlags && this.selectedFlags.length) {
        params.push("flag=" + encodeURIComponent(this.selectedFlags.join(",")));
      }
      const qs = params.length ? "?" + params.join("&") : "";
      this.rows = (await this.$http.get("api/studentFlags/getFlags" + qs)) || [];
      this.selected = [];
    },
    downloadSample() {
      const rows = [
        ["StudentID", "Term", "Flag"],
        ["0035126", "RG2027", "RG2027 Girls"],
      ];
      for (let i = 0; i < 20; i++) {
        rows.push(["", "", ""]);
      }

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [{ wch: 14 }, { wch: 12 }, { wch: 22 }];
      Object.keys(ws).forEach((cell) => {
        if (cell[0] === "A" && cell !== "A1" && ws[cell]) {
          ws[cell].t = "s";
          ws[cell].z = "@";
        }
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "StudentFlags");
      XLSX.writeFile(wb, "StudentFlags_Sample.xlsx");
    },
    openImportDialog() {
      this.preview = null;
      this.previewError = "";
      this.fileName = "";
      this.importDialog = true;
      this.$nextTick(() => {
        if (this.$refs.flagFile) this.$refs.flagFile.value = "";
      });
    },
    closeImport() {
      this.importDialog = false;
      this.preview = null;
      this.fileName = "";
    },
    async onFilePicked(event) {
      const files = event.target.files;
      if (!files || !files.length) return;
      this.fileName = files[0].name;
      this.previewing = true;
      this.previewError = "";
      try {
        const formData = new FormData();
        formData.append("upload_file", files[0]);
        const res = await this.$http.post(
          "api/studentFlags/previewImport",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (res && res.error) {
          this.previewError = res.error;
          this.preview = null;
        } else {
          this.preview = res;
        }
      } catch (e) {
        console.error(e);
        this.previewError =
          (e.response && e.response.data) || "Failed to read file";
      } finally {
        this.previewing = false;
      }
    },
    importableRows() {
      if (!this.preview || !this.preview.rows) return [];
      const allowed = { ok: true, update: true, unchanged: true, duplicate: true };
      return this.preview.rows.filter(
        (r) =>
          allowed[r.status] &&
          r.studentID &&
          r.term &&
          r.flag
      );
    },
    async saveImport() {
      const rows = this.importableRows().map((r) => ({
        studentID: r.studentID,
        term: r.term,
        flag: r.flag,
      }));
      if (!rows.length) return;
      this.savingImport = true;
      try {
        const chunkSize = 200;
        let inserted = 0;
        let updated = 0;
        let skipped = 0;
        for (let i = 0; i < rows.length; i += chunkSize) {
          const chunk = rows.slice(i, i + chunkSize);
          const res = await this.$http.post(
            "api/studentFlags/saveImport",
            chunk
          );
          inserted += (res && res.inserted) || 0;
          updated += (res && res.updated) || 0;
          skipped += (res && res.skipped) || 0;
        }
        this.closeImport();
        await this.loadFlagNames();
        await this.loadFlags();
        this.$notify({
          group: "notification",
          text:
            "Imported " +
            inserted +
            " new, updated " +
            updated +
            ", skipped " +
            skipped,
          type: "success",
        });
      } catch (e) {
        console.error(e);
        this.previewError = "Failed to save flags";
      } finally {
        this.savingImport = false;
      }
    },
    confirmDelete() {
      if (!this.selected.length) return;
      this.deleteDialog = true;
    },
    async doDelete() {
      const ids = this.selected.map((r) => r.id);
      this.deleting = true;
      try {
        await this.$http.post("api/studentFlags/deleteFlags", ids);
        this.deleteDialog = false;
        await this.loadFlagNames();
        await this.loadFlags();
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to delete flags";
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.v-datatable >>> .v-input--selection-controls.primary--text,
.v-datatable >>> .v-input--selection-controls.accent--text {
  color: #4caf50 !important;
}
.v-datatable >>> .v-input--selection-controls.primary--text .v-icon,
.v-datatable >>> .v-input--selection-controls.accent--text .v-icon {
  color: #4caf50 !important;
}
</style>
